import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Check, X } from "lucide-react";
import { api, formatErr } from "@/lib/api";

// Discreet chip that shows any friend-dual invites still awaiting this guest's
// answer. Buttons stay visible until they Accept or Decline — the invited guest
// sees it in both /menu (their own device) and /cabin (the iPad).
export default function PendingInviteChip({ guestId }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!guestId) { setItems([]); return; }
    try {
      const { data } = await api.get("/public/bookings/pending-invites", { params: { guest_id: guestId } });
      setItems(Array.isArray(data) ? data : []);
    } catch { /* silent — never break the page over a chip */ }
  }, [guestId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const respond = async (bid, accept) => {
    setBusy(bid);
    try {
      await api.post(`/public/bookings/${bid}/respond-invite`, { guest_id: guestId, accept });
      toast.success(accept ? "Invite accepted — see you at the spa" : "Invite declined");
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not respond");
    } finally { setBusy(null); }
  };

  if (!items.length) return null;

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="space-y-2" data-testid="pending-invite-chips">
      {items.map((b) => (
        <div
          key={b.id}
          className="bg-emerald-500/10 border border-emerald-600/40 p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4"
          data-testid={`pending-invite-${b.id}`}
        >
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <UserPlus className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="overline text-emerald-700 dark:text-emerald-400 text-[10px]">Friend dual · confirmation pending</div>
              <div className="text-sm mt-1">
                <span className="font-medium">{b.inviter_name || "A guest"}</span>
                {b.inviter_cabin ? <span className="text-muted-foreground"> from <em>{b.inviter_cabin}</em></span> : null}
                {" "}has booked you into
                <span className="font-medium"> {b.treatment_name}</span> — <span className="font-mono">{fmt(b.start_at)}</span>.
              </div>
              {b.notes && <div className="text-[11px] text-muted-foreground mt-1 italic">"{b.notes}"</div>}
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => respond(b.id, true)}
              disabled={busy === b.id}
              className="inline-flex items-center gap-1 px-4 h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50"
              data-testid={`invite-accept-${b.id}`}
            ><Check className="h-3.5 w-3.5" /> Accept</button>
            <button
              onClick={() => respond(b.id, false)}
              disabled={busy === b.id}
              className="inline-flex items-center gap-1 px-4 h-9 rounded-full border border-border hover:border-destructive hover:text-destructive text-sm disabled:opacity-50"
              data-testid={`invite-decline-${b.id}`}
            ><X className="h-3.5 w-3.5" /> Decline</button>
          </div>
        </div>
      ))}
    </div>
  );
}
